import $axios from 'axios';
import type { AxiosError } from 'axios';
import { Sentry } from '@/config/sentry';

export const axios = $axios.create();

axios.defaults.headers['Accept-Language'] = 'en';
axios.defaults.headers.post['Content-Type'] = 'application/json';
axios.defaults.baseURL = import.meta.env.VITE_API_URL + '/api/v1/';
axios.defaults.withCredentials = true;

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    if ($axios.isCancel(error)) {
      return Promise.reject({
        cancel: true,
        message: 'The endpoint was cancelled',
      });
    }

    const axiosError = error as AxiosError;

    if (axiosError.response) {
      reportToSentry(axiosError);

      if (axiosError.response.status === 401) {
        window.location.replace('/public/login');
        return Promise.reject(axiosError.response);
      }

      // A 403 usually means the cookie now belongs to a different account
      // (someone logged in as another user in a second tab). Ask the shell to
      // re-check who is signed in so it can redirect instead of rendering a
      // page that will keep failing.
      if (axiosError.response.status === 403) {
        window.dispatchEvent(new Event('whm:forbidden'));
      }

      return Promise.reject(axiosError.response);
    }

    reportToSentry(axiosError);
    return Promise.reject(error);
  },
);

function reportToSentry(error: AxiosError) {
  const status = error.response?.status;
  // Skip routine session-expired noise; surface auth controller failures too if they're not on /auth/me-style endpoints
  if (status === 401 && isLikelySessionExpiry(error)) return;
  // A request that never reached the server (offline, tab suspended, flaky
  // wifi) on the session-check endpoints is client connectivity, not a bug.
  if (!error.response && error.code === 'ERR_NETWORK' && isLikelySessionExpiry(error)) return;

  Sentry.withScope((scope) => {
    scope.setTag('http.status', String(status ?? 'network'));
    scope.setTag('http.method', error.config?.method?.toUpperCase() ?? 'UNKNOWN');
    scope.setTag('http.url', stripBaseUrl(error.config?.url ?? ''));
    scope.setContext('response', {
      status,
      statusText: error.response?.statusText,
      data: error.response?.data,
    });
    Sentry.captureException(error);
  });
}

function isLikelySessionExpiry(error: AxiosError): boolean {
  const url = error.config?.url ?? '';
  // 401s on the session-check endpoints are routine. Everything else (e.g. a 401 on /vision/*) is a real issue.
  // URLs here are relative to baseURL ("auth/status"), so the leading slash is optional.
  return /^\/?(auth|profile)\/(me|session|check|status)/.test(url);
}

function stripBaseUrl(url: string): string {
  return url.replace(import.meta.env.VITE_API_URL ?? '', '');
}
